import { useState, useEffect, useRef } from 'react';
import utils from 'utils';

interface UseLoadingMessagesProps {
  loading: boolean;
}

const useLoadingMessages = ({
  loading,
}: UseLoadingMessagesProps): { loadingMessage: string; clearLoadingState: () => void } => {
  const t = utils.intl.useTranslator();
  const [loadingMessage, setLoadingMessage] = useState('');
  const messageIntervalRef = useRef<number | null>(null);
  const messageIndexRef = useRef<number>(0);
  const dotIndexRef = useRef<number>(0);

  // Function to clear all intervals and reset loading state
  const clearLoadingState = () => {
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }

    setLoadingMessage('');
  };

  useEffect(() => {
    if (!loading) {
      clearLoadingState();
      return;
    }

    const dotsSequence = ['.', '..', '...', '..'];
    const messages = [
      utils.app.textTranslated('chatbotLoadingMsg1', t, 'Thinking'),
      utils.app.textTranslated('chatbotLoadingMsg2', t, 'Still thinking'),
      utils.app.textTranslated('chatbotLoadingMsg3', t, 'Thinking hard'),
      utils.app.textTranslated('chatbotLoadingMsg4', t, 'Sorry for the delay, almost there'),
      utils.app.textTranslated('chatbotLoadingMsg5', t, 'Processing your request'),
      utils.app.textTranslated('chatbotLoadingMsg6', t, 'Just a moment'),
      utils.app.textTranslated('chatbotLoadingMsg7', t, 'Working on it'),
    ];

    dotIndexRef.current = 0;
    messageIndexRef.current = 0;

    // Set initial state - combine message and dots
    setLoadingMessage(`${messages[messageIndexRef.current]}${dotsSequence[dotIndexRef.current]}`);

    // Dots interval - changes every 0.5 second
    const dotsInterval = setInterval(() => {
      dotIndexRef.current = (dotIndexRef.current + 1) % dotsSequence.length;
      setLoadingMessage(`${messages[messageIndexRef.current]}${dotsSequence[dotIndexRef.current]}`);
    }, 500);

    // Store dots interval in a ref so we can clear it
    const dotsIntervalRef = { current: dotsInterval };

    // Message interval - changes every 3 seconds
    messageIntervalRef.current = setInterval(() => {
      messageIndexRef.current = (messageIndexRef.current + 1) % messages.length;
      setLoadingMessage(`${messages[messageIndexRef.current]}${dotsSequence[dotIndexRef.current]}`);
    }, 5000);

    // Cleanup function
    return () => {
      clearInterval(dotsIntervalRef.current);
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
    };
  }, [loading]);

  return { loadingMessage, clearLoadingState };
};

export default useLoadingMessages;
